import CreateTaskTags from "@/components/CreateTaskTags/createTaskTags.component";
import CustomLoader from "@/components/CustomLoader/customLoader.component";
import QuillTextEditor from "@/components/RichTextEditor/QuillTextEditor/quillTextEditor.component";
import { useMediaQuery } from "@/hooks/AppContext/mediaQuery";
import { useUser } from "@/hooks/AppContext/user.hook";
import useNavigateToTask from "@/hooks/TaskNavigation/useNavigateToTask.hook";
import { createTask } from "@/models/tasks.model";
import { AppContext } from "@/services/AppContext/appContext";
import { Button } from "@/shadcn/components/ui/button";
import { Calendar } from "@/shadcn/components/ui/calendar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shadcn/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shadcn/components/ui/dialog";
import { Input } from "@/shadcn/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shadcn/components/ui/popover";
import { ScrollArea } from "@/shadcn/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcn/components/ui/tooltip";
import { NewTask, NewTaskTag } from "@/types";
import {
  formatDueDate,
  genericFailureToast,
  genericSuccessToast,
} from "@/utils/utils";
import { set } from "date-fns";
import { CalendarIcon, Check, ChevronsUpDown, User } from "lucide-react";

import React, { useContext, useState } from "react";
type CreateTaskDialogProps = {
  children: React.ReactNode;
  parentID?: number;
};

const CreateTaskDialog: React.FC<CreateTaskDialogProps> = ({
  children,
  parentID,
}) => {
  const { user, allAppUsers, initialGetUserTasks, getPrecacheTasks } =
    useContext(AppContext);
  const { navigateToTask } = useNavigateToTask();

  const [newTask, setNewTask] = useState<NewTask>({
    assigned_to: user.id,
    title: "",
    due_date: formatDueDate(new Date()),
    input: "",
    collaborators: [user.id],
    tags: [],
    parent_id: parentID,
  });
  const [creationState, setCreationState] = useState<
    "creation" | "submitting" | "success" | "failed"
  >("creation");
  const [isOpen, setIsOpen] = useState(false);
  const [openCollaborators, setOpenCollaborators] = useState(false);
  const { getUserFullNameByID, getUserProfileImageByID } = useUser();

  function handleEditField(field: keyof NewTask, value: string) {
    setNewTask((prev) => ({ ...prev, [field]: value }));
  }

  function handleFieldAssignedTo(value: string) {
    try {
      const assigned_to = Number(value);
      if (isNaN(assigned_to)) {
        return;
      }
      setNewTask((prev) => ({ ...prev, assigned_to }));
    } catch (e) {
      console.log(e);
    }
  }

  function handleFieldCollaborators(collaboratorId: number) {
    console.log(collaboratorId);
    setNewTask((prev) => {
      const updatedCollaborators = prev.collaborators?.includes(collaboratorId)
        ? prev.collaborators.filter((id) => id !== collaboratorId)
        : [...(prev.collaborators || []), collaboratorId];
      return { ...prev, collaborators: updatedCollaborators };
    });
  }

  function handleFieldDueDate(value: Date | undefined) {
    if (!value) {
      setNewTask((prev) => ({ ...prev, due_date: null }));
      return;
    }
    const date = set(new Date(), {
      year: value.getFullYear(),
      month: value.getMonth(),
      date: value.getDate(),
    });
    // YYYY-MM-DD
    const formatted = formatDueDate(date);
    setNewTask((prev) => ({ ...prev, due_date: formatted }));
  }

  function handleFieldTags(newTag: NewTaskTag) {
    console.log(newTag.tag, newTag.color, newTag.category_id);
    setNewTask((prev) => {
      const updatedTags = prev.tags?.some((t) => t.tag === newTag.tag)
        ? prev.tags.filter((t) => t.tag !== newTag.tag)
        : [...(prev.tags || []), newTag];
      return { ...prev, tags: updatedTags };
    });
  }

  async function handleSubmit() {
    setCreationState("submitting");
    const response = await createTask(newTask);
    console.log(response);
    if (response.rStatus === "error") {
      console.log(response.message);
      setCreationState("failed");
      genericFailureToast("Failed to create task");
      return;
    }

    console.log("Task created successfully");

    genericSuccessToast("Task created successfully", {
      position: "bottom-left",
      actionLabel: "Go to task",
      callback: () => navigateToTask(response.taskID),
    });

    setCreationState("success");
    await closeDialog();
    await initialGetUserTasks({ force: true });
    await getPrecacheTasks(false, true);
  }

  async function closeDialog() {
    // clear the form
    setNewTask({
      assigned_to: user.id,
      title: "",
      due_date: formatDueDate(new Date()),
      input: "",
      collaborators: [],
      tags: [],
      parent_id: parentID,
    });
    // close the dialog
    setIsOpen(false);
    setCreationState("creation");
  }

  function clearState(open: boolean) {
    setIsOpen(open);
    setCreationState("creation");
    setNewTask({
      assigned_to: user.id,
      title: "",
      due_date: formatDueDate(new Date()),
      input: "",
      collaborators: [user.id],
      tags: [],
      parent_id: parentID,
    });
  }

  const getErrorMessages = () => {
    const errors = [];
    if (!newTask.title) {
      errors.push("Title is required");
    } else if (newTask.title.length > 254) {
      errors.push("Title is too long");
    }
    if (!newTask.assigned_to) {
      errors.push("Assignee is required");
    }
    if (newTask.collaborators?.length === 0) {
      errors.push("At least one collaborator is required");
    }
    // Handled on backend
    // if (!hasProjectManager(newTask.collaborators || [])) {
    //   errors.push("Include one project manager to collaborators");
    // }
    return errors;
  };

  const isNotSmall = useMediaQuery("sm");

  return (
    <Dialog open={isOpen} onOpenChange={clearState}>
      <DialogDescription className="hidden">
        Create {parentID && "Sub "}Task
      </DialogDescription>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className="sm:max-w-[700px] !rounded-sm"
        onInteractOutside={(e) => {
          if (creationState === "creation" || creationState === "submitting") {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-center">
            Create {parentID && "Sub "}Task
          </DialogTitle>
        </DialogHeader>
        {creationState === "creation" ? (
          <>
            <div className="grid grid-cols-[1fr_3fr] gap-2 items-center">
              <label className="justify-self-end mr-4" htmlFor="create-title">
                Title
              </label>
              <Input
                id="create-title"
                value={newTask.title}
                onChange={(e) => handleEditField("title", e.target.value)}
                maxLength={255}
              />
              <label
                className="justify-self-end mr-4"
                htmlFor="create-description"
              >
                Description
              </label>
              <div className="w-full">
                <QuillTextEditor
                  key={`create-description`}
                  id={`create-description`}
                  value={newTask.input || ""}
                  onChange={handleEditField.bind(null, "input")}
                  placeholder="No description yet"
                  readOnly={false}
                />
              </div>
              <label
                className="justify-self-end mr-4"
                htmlFor="create-assignee"
              >
                Assigned to
              </label>
              <Popover modal>
                <PopoverTrigger asChild>
                  <Button
                    id="create-assignee"
                    variant="outline"
                    className="justify-between w-full"
                  >
                    {newTask.assigned_to ? (
                      <div className="flex items-center gap-2">
                        {getUserProfileImageByID(newTask.assigned_to) ? (
                          <img
                            className="size-4 rounded-full bg-primary shrink-0"
                            src={getUserProfileImageByID(newTask.assigned_to)}
                            alt={getUserFullNameByID(newTask.assigned_to)}
                          />
                        ) : (
                          <User className="size-4 rounded-full bg-primary shrink-0" />
                        )}
                        <div>{getUserFullNameByID(newTask.assigned_to)}</div>
                      </div>
                    ) : (
                      "Select assignee"
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-full p-0"
                  side={isNotSmall ? "right" : undefined}
                  align={isNotSmall ? "start" : undefined}
                >
                  <Command>
                    <CommandInput placeholder="Search assignee..." />
                    <CommandList>
                      <ScrollArea className="h-[200px]">
                        <CommandEmpty>No assignees found.</CommandEmpty>
                        <CommandGroup>
                          {allAppUsers.map((user) => (
                            <CommandItem
                              key={user.id}
                              onSelect={() =>
                                handleFieldAssignedTo(user.id.toString())
                              }
                            >
                              <Check
                                className={`mr-2 h-4 w-4 ${
                                  newTask.assigned_to === user.id
                                    ? "opacity-100"
                                    : "opacity-0"
                                }`}
                              />
                              <div className="flex items-center gap-2">
                                {user.profile_picture ? (
                                  <img
                                    className="size-4 rounded-full bg-primary shrink-0"
                                    src={user.profile_picture}
                                    alt={user.first_name + " " + user.surname}
                                  />
                                ) : (
                                  <User className="size-4 rounded-full bg-primary shrink-0" />
                                )}
                                <div>
                                  {user.first_name} {user.surname}
                                </div>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </ScrollArea>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <label
                className="justify-self-end mr-4"
                htmlFor="create-due-date"
              >
                Due Date
              </label>
              <Popover modal>
                <PopoverTrigger id="create-due-date" asChild>
                  <Button
                    variant={"outline"}
                    className={`w-full justify-start text-left font-normal
                      ${!newTask.due_date && "text-muted-foreground"}`}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {newTask.due_date ? (
                      formatDueDate(newTask.due_date)
                    ) : (
                      <span>Pick a date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  side={isNotSmall ? "right" : undefined}
                  align={isNotSmall ? "start" : undefined}
                >
                  <Calendar
                    mode="single"
                    selected={
                      newTask.due_date ? new Date(newTask.due_date) : undefined
                    }
                    onSelect={(date) => handleFieldDueDate(date)}
                    weekStartsOn={1}
                  />
                </PopoverContent>
              </Popover>
              <label
                className="justify-self-end mr-4"
                htmlFor="create-collaborators"
              >
                Collaborators
              </label>
              <Popover
                open={openCollaborators}
                onOpenChange={setOpenCollaborators}
                modal
              >
                <PopoverTrigger asChild>
                  <Button
                    id="create-collaborators"
                    variant="outline"
                    role="combobox"
                    aria-expanded={openCollaborators}
                    className="justify-between w-full"
                  >
                    {newTask.collaborators && newTask.collaborators.length > 0
                      ? `${newTask.collaborators.length} selected`
                      : "Select collaborators"}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-full p-0"
                  side={isNotSmall ? "right" : undefined}
                  align={isNotSmall ? "start" : undefined}
                >
                  <Command>
                    <CommandInput placeholder="Search collaborators..." />
                    <CommandList>
                      <ScrollArea className="h-[200px]">
                        <CommandEmpty>No collaborators found.</CommandEmpty>
                        <CommandGroup>
                          {allAppUsers.map((collaborator) => (
                            <CommandItem
                              key={collaborator.id}
                              onSelect={() =>
                                handleFieldCollaborators(collaborator.id)
                              }
                            >
                              <Check
                                className={`mr-2 h-4 w-4 ${
                                  newTask.collaborators?.includes(
                                    collaborator.id
                                  )
                                    ? "opacity-100"
                                    : "opacity-0"
                                }`}
                              />
                              <div className="flex items-center gap-2">
                                {collaborator.profile_picture ? (
                                  <img
                                    className="size-4 rounded-full bg-primary shrink-0"
                                    src={collaborator.profile_picture}
                                    alt={
                                      collaborator.first_name +
                                      " " +
                                      collaborator.surname
                                    }
                                  />
                                ) : (
                                  <User className="size-4 rounded-full bg-primary shrink-0" />
                                )}
                                <div>
                                  {collaborator.first_name}{" "}
                                  {collaborator.surname}
                                </div>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </ScrollArea>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <label className="justify-self-end mr-4" htmlFor="create-tags">
                Tags
              </label>
              <CreateTaskTags
                id="create-tags"
                taskTags={newTask.tags!}
                handleFieldTags={handleFieldTags}
                createTagAllowed={true}
                side={isNotSmall ? "right" : undefined}
                modal={true}
              />
            </div>
            <DialogFooter>
              <TooltipProvider>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <div className="flex justify-end">
                      <Button
                        type="submit"
                        onClick={handleSubmit}
                        disabled={getErrorMessages().length > 0}
                        className="justify-self-end"
                      >
                        Create Task
                      </Button>
                    </div>
                  </TooltipTrigger>
                  {getErrorMessages().length > 0 && (
                    <TooltipContent side="left" className="mr-3">
                      {getErrorMessages().map((error, index) => (
                        <div key={index}>{error}</div>
                      ))}
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </DialogFooter>
          </>
        ) : creationState === "submitting" ? (
          <CustomLoader size="sm" />
        ) : creationState === "success" ? (
          <DialogDescription>Task created successfully</DialogDescription>
        ) : (
          <DialogDescription>Failed to create task</DialogDescription>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreateTaskDialog;
